/**
 * 企业微信环境判定与「未登录落点」计算（BUG-015）
 *
 * ⭐ 设计原则：**判定逻辑只写一次**，所有「未登录 → 落点」入口
 * （路由守卫 / axios 401 / axios 422 / 登出）统一调用本模块，
 * ⇒ 从结构上杜绝「修一个漏一个」。
 *
 * ⭐ 纯函数模块：**无 Vue 依赖** ⇒ 可被 Node 脚本直接断言验证。
 *
 * @see .project/wechat/BUG-015_代开发应用加载页面未触发Agent微信自动登录/
 */

/** 代开发应用（Agent）微信授权登录页路由（固定落点） */
export const AGENT_LOGIN_PATH = '/login/wechat/Agent'

/** 通用账号密码登录页路由（非企微容器内的原有落点） */
export const PASSWORD_LOGIN_PATH = '/login'

/**
 * ⭐ 代开发应用「应用可信域名」（OAuth 的 `redirect_uri` **必须**落在此域名下）。
 *
 * 企业微信硬性要求：`redirect_uri` 的域名必须在该应用的
 * 「网页授权及JS-SDK → 可信域名」中登记，否则授权会被拒绝。
 *
 * ⚠️ 因此 `redirect_uri` **不得**直接取 `window.location.href`（会随访问域名漂移，
 *    例如通过 IP / 别名域名 / localhost 访问时必然失败），此处**固定**为可信域名。
 *
 * @see .deploy/image-3.png（企业微信服务商后台登记 `ww.yicengkeji.com`）
 */
export const AGENT_REDIRECT_ORIGIN = 'https://ww.yicengkeji.com'

/**
 * ⭐ 代开发应用 OAuth 的 `redirect_uri` **落点路径**。
 *
 * 🔴 **BUG-015 第十轮定稿：用根路径 `''`（与【已上线】应用主页逐字一致）。**
 *
 * 依据：服务商后台「代开发应用审核详情（已上线）」显示
 *   应用主页 = https://ww.yicengkeji.com（根路径，无 /login、无末尾斜杠）
 * ⇒ redirect_uri 必须与已上线的应用主页同域同路径 ⇒ 用根路径。
 *
 * ⚠️ 演进史：第二轮改根路径 → 第六/七/八轮反复试 agentid → 第十轮曾按「编辑中版本」
 *    的主页（/login）改为 /login/wechat/Agent，但【生效的是已上线版（根路径）】⇒ 改回 ''。
 *    ⛔ 若后台应用主页将来改为 /login，此常量必须同步改 '/login/wechat/Agent'。
 *
 * ⚠️ 代价：授权回来后 code 落在根路径（首页 Funnel.vue 自身无 code 处理逻辑）
 *    ⇒ 由路由守卫（isAgentCallback）拦截后转交授权页兑换（该机制第二轮已实现）。
 */
export const AGENT_REDIRECT_PATH = ''

/**
 * ⭐ 判断当前请求是否为「代开发应用 OAuth 回调」（即 URL 上带着授权码）。
 *
 * 企业微信 OAuth 回跳会带 `code`（代开发部分场景为 `auth_code`）。
 * ⚠️ 必须**同时**满足以下条件才算回调，⛔ 避免误伤普通带参请求：
 * - 处于企业微信容器内（`wxwork` UA）—— 容器外不存在代开发回调
 * - URL 上存在 `code` 或 `auth_code`
 * - ⛔ 当前**不在**授权页自身（否则会与授权页内的 `onMounted` 处理重复）
 *
 * @param {string} fullPath 目标路径（含 query），如 `/?code=xxx&state=yyy`
 * @param {string} [userAgent] UA 字符串；缺省取 `navigator.userAgent`
 * @returns {boolean}
 */
export function isAgentCallback(fullPath, userAgent) {
  if (!isInWxWork(userAgent)) return false
  const path = typeof fullPath === 'string' ? fullPath : ''
  // 已在授权页 ⇒ 交给授权页自身处理（⛔ 不重复拦截，防死循环）
  if (path.startsWith(AGENT_LOGIN_PATH)) return false
  const queryIndex = path.indexOf('?')
  if (queryIndex < 0) return false
  const query = path.slice(queryIndex + 1)
  const hasAuthCode = /(?:^|&)(?:code|auth_code)=/.test(query)
  return hasAuthCode
}

/**
 * ⭐ 代开发应用 OAuth 相关 ID 常量 —— 值必须与 `backend/config.py` **逐字对应**。
 *
 * ⚠️⚠️ **BUG-015 第十二轮定稿（最终）：`appid` 必须用【授权企业】的 CorpID。**
 *
 * 根因（社区同款问题 + 用户确认）：
 *   代开发应用 OAuth 的 `appid` = **授权企业（测试企业有限公司）的 CorpID**，
 *   ⛔ **不是**服务商公司自己的 CorpID。
 *   因为用户是在「测试企业有限公司」的企业微信里点开应用 ⇒ 企微按 appid 找「该企业下授权的应用」。
 *   用服务商 CorpID（wwd6ba440a130d4e48）⇒ 找不到 ⇒ 「应用不存在」（-7000117）。
 *
 * | 本常量 | 值 | OAuth 用途 |
 * |--------|-----|-----------|
 * | ⭐ `WECHAT_CORP_ID` | `ww998a5b5d4c4cbf27` | ⭐ **`appid`（授权企业 CorpID）** |
 * | ⭐ `WECHAT_AGENT_AGENT_ID` | `1000013` | ⭐ **`agentid`（应用 agentid）** |
 *
 * 授权企业「测试企业有限公司」的企业ID = `ww998a5b5d4c4cbf27`（企微后台「企业信息」确认）。
 * ⛔ `wwd6ba440a130d4e48` 是【服务商】CorpID，不可用作 OAuth appid。
 */
export const WECHAT_CORP_ID = 'ww998a5b5d4c4cbf27'

/** ⭐ 代开发应用模板ID（官方术语 `suite_id`，后台「代开发应用模板信息 → 模板ID」） */
export const WECHAT_AGENT_TEMPLATE_ID = 'dkb3e3ede568cd83da'

/** ⭐ 代开发应用在企业内的 agentid（纯数字）。
 *
 * 🔴 BUG-015 第十七轮（新项目）：agentid 演进 `1000012` → `1000013` → **`1000016`**
 *   （代开发应用重建后，企微下发的应用 agentid 会变；以 `enter_agent` 回调
 *     下发的 `AgentID` 为准 —— 实测 2026-10-14 回调为 `1000016`）。
 *   ⛔ 必须与企微后台「应用管理 → 智能管理 → AgentId」逐字一致，
 *      用错会报「应用不存在」(-7000117)。
 */
export const WECHAT_AGENT_AGENT_ID = '1000016'

/**
 * ⭐ 代开发应用 OAuth 的 `scope`。
 *
 * ⭐ **BUG-015 第六轮定稿：严格采用腾讯官方指南值 `snsapi_base`。**
 *
 * 官方指南原文（用户提供）：
 * ```
 * https://open.weixin.qq.com/connect/oauth2/authorize
 *   ?appid=CORPID&redirect_uri=REDIRECT_URI&response_type=code
 *   &scope=snsapi_base&state=STATE&agentid=AGENTID#wechat_redirect
 * ```
 *
 * ⚠️ 可选值：
 * | 值 | 语义 |
 * |----|------|
 * | ⭐ `snsapi_base`（**当前采用**） | **静默授权**（无需用户确认，仅取 userid） |
 * | `snsapi_privateinfo` | **手动授权**（可获取更多敏感信息，首次有确认页） |
 *
 * 🔴 我曾在第五轮改为 `snsapi_privateinfo`（依据二手博客）—— 属**偏航**，第六轮已回到官方值。
 */
export const AGENT_SCOPE = 'snsapi_base'

/**
 * 构造代开发应用 OAuth 授权 URL（⭐ **三处授权页共用，单一构造源** —— BP-017）。
 *
 * ⭐ **严格对齐腾讯官方指南**（BUG-015 第六轮定稿）：
 * ```
 * https://open.weixin.qq.com/connect/oauth2/authorize
 *   ?appid=CORPID&redirect_uri=REDIRECT_URI&response_type=code
 *   &scope=snsapi_base&state=STATE&agentid=AGENTID#wechat_redirect
 * ```
 *
 * | 参数 | 官方填法 | 本项目值 |
 * |------|---------|---------|
 * | ⭐ `appid` | **授权企业 CorpID** | `ww998a5b5d4c4cbf27` |
 * | ⭐ `agentid` | **应用 agentid** | `1000013` |
 * | `scope` | `snsapi_base` / `snsapi_privateinfo` | `AGENT_SCOPE` |
 * | `redirect_uri` | 须在应用「可信域名」下 | `https://ww.yicengkeji.com` |
 *
 * ⚠️ **`appid` 用【授权企业】CorpID**（官方：`appid=CORPID`）
 *    ⛔ 不可用服务商 CorpID `wwd6ba440a130d4e48`（会报「应用不存在」-7000117 或 40001）
 * ⚠️ **`agentid` 用应用 agentid（1000013）**，⛔ 不是模板ID（官方：`agentid=AGENTID`，
 *    即应用在企业内的真实 agentid）
 * ⚠️ **`redirect_uri` 用根路径**（与【已上线】应用主页 `https://ww.yicengkeji.com` 逐字一致）
 *
 * 第十七轮修正（新项目）：agentid 由 1000012 → 1000013（新代开发应用重新授权的权威值）
 *
 * @param {string} state 防 CSRF 的随机 state
 * @returns {string} 可直接赋给 `window.location.href` 的授权 URL
 */
export function buildAgentAuthorizeUrl(state) {
  const redirectUri = encodeURIComponent(AGENT_REDIRECT_ORIGIN + AGENT_REDIRECT_PATH)
  return (
    'https://open.weixin.qq.com/connect/oauth2/authorize' +
    `?appid=${WECHAT_CORP_ID}` +
    `&redirect_uri=${redirectUri}` +
    '&response_type=code' +
    `&scope=${AGENT_SCOPE}` +
    `&state=${encodeURIComponent(state)}` +
    `&agentid=${WECHAT_AGENT_AGENT_ID}` +
    '#wechat_redirect'
  )
}

/**
 * 判定当前是否处于「企业微信」内置浏览器（容器）内。
 *
 * 企业微信各端 UA 均包含 `wxwork`，例如：
 * - Windows：`Mozilla/5.0 ... wxwork/4.x.x MicroMessenger/7.x ...`
 * - iOS/Android：`... wxwork/x.x.x ...`
 *
 * ⚠️ 大小写不敏感（`WxWork` / `WXWORK` 均命中）。
 *
 * @param {string} [userAgent] UA 字符串；缺省取 `navigator.userAgent`
 * @returns {boolean}
 */
export function isInWxWork(userAgent) {
  const ua =
    typeof userAgent === 'string'
      ? userAgent
      : (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  return /wxwork/i.test(ua)
}

/**
 * 计算「未登录时应去的落点」。
 *
 * - **企微容器内**（代开发应用入口）⇒ 走 `AGENT_LOGIN_PATH`，触发企业微信自动授权
 * - **容器外**（普通浏览器）⇒ 走 `PASSWORD_LOGIN_PATH`，**行为与改动前完全一致**
 *
 * `redirectPath` 会作为 `redirect` 查询参数透传，登录成功后由登录页回跳。
 *
 * @param {string} [redirectPath] 登录成功后要回到的路径（可空）
 * @param {string} [userAgent] UA 字符串；缺省取 `navigator.userAgent`
 * @returns {{ path: string, query: { redirect?: string } }}
 */
export function resolveLoginTarget(redirectPath, userAgent) {
  const path = isInWxWork(userAgent) ? AGENT_LOGIN_PATH : PASSWORD_LOGIN_PATH
  const redirect = typeof redirectPath === 'string' ? redirectPath.trim() : ''
  return redirect ? { path, query: { redirect } } : { path, query: {} }
}

/**
 * 供 `window.location.href` 场景（axios 拦截器，无法用 vue-router）使用的 URL 构造器。
 *
 * ⚠️ 入口 #3/#4 的 axios 拦截器使用 `window.location.href` **硬跳转**
 * （**绕过 vue-router 守卫**）⇒ 必须与路由守卫共用同一判定，否则「修一个漏一个」。
 *
 * @param {string} [redirectPath] 登录成功后要回到的路径（可空）
 * @param {string} [userAgent] UA 字符串；缺省取 `navigator.userAgent`
 * @returns {string} 可直接赋给 `window.location.href` 的 URL
 */
export function buildLoginUrl(redirectPath, userAgent) {
  const { path, query } = resolveLoginTarget(redirectPath, userAgent)
  return query.redirect ? `${path}?redirect=${encodeURIComponent(query.redirect)}` : path
}
